/**
 * GameplayConfig
 *
 * Mixed-topic gameplay values. Sub-topics with high tuning value have
 * been split out to topic-scoped configs that follow the canonical
 * defaults+dev-override pattern (`docs/plans/2026-04-25-CONFIG-MIGRATION.md`):
 *
 *   - `mobAI`  → `src/shared/config/mob-ai.config.ts` (`MOB_AI`)
 *   - `loot` + `drops` → `src/shared/config/loot.config.ts` (`LOOT`)
 *   - `regen` → `src/shared/config/regen.config.ts` (`REGEN`)
 *
 * The split-out fields are still re-exported from `GAMEPLAY_CONFIG` for
 * back-compat (existing consumers like MobAISystem, MobLogic, CombatHandler
 * keep working without edits). Migrate consumers per-file at your leisure.
 *
 * Topics still living here (not yet migrated): death/respawn/iframes,
 * GCD, attack ranges, targeting, anti-cheat, persistence, items,
 * progression, shop, party, interaction ranges, timers.
 */

import { MOB_AI } from '../config/mob-ai.config';
import { LOOT } from '../config/loot.config';
import { REGEN } from '../config/regen.config';

export const GAMEPLAY_CONFIG = {
  respawnDelay: 2000,
  iFrameDuration: 1500,
  gcdDuration: 500,
  autoSaveIntervalMs: 60000,
  persistence: {
    maxSaveSlots: 5,
    saveVersion: '1.1.0',
    saveKey: 'helix2000_saves'
  },
  attackRangeMelee: 120,
  /**
   * COMBAT-MELEE-CONE-01 — basic melee sweeps a forward wedge instead of
   * resolving one nearest enemy, and needs no selected target.
   *
   * `arcDeg` is the FULL opening angle, so 100 = 50 either side of facing —
   * forgiving enough to catch a cluster you walked into (mobs sit at slightly
   * different y on platforms), tight enough to read as directional. Anything
   * behind you is always safe. `maxTargets` bounds a swing so a large pull
   * cannot be cleared with one button; 0 means uncapped.
   */
  meleeConeArcDeg: 100,
  meleeConeMaxTargets: 5,
  /**
   * Reach of the melee cone, in pixels — SEPARATE from `attackRangeMelee`.
   *
   * `attackRangeMelee` (120) is the single-target envelope the server's
   * anti-cheat gate uses for a direct melee hit. A cone is an area sweep and is
   * bounded instead by `antiCheat.maxAreaRadius` (300), so this may legally sit
   * anywhere up to that ceiling.
   *
   * Tuning history, measured on the first_field pig cluster with
   * `scripts/qa/melee-cone-probe.ts`:
   *   120 — effectively single-target; mobs sit further apart than that
   *   300 — validated the mechanism, but read as a shockwave (4/4 incl. 236px)
   *   170 — 3 hit at 76/118/171px, straggler at 231px spared
   *   150 — CURRENT. Same cluster cleave, tighter envelope; 230px still spared
   *
   * NOTE this is a GLOBAL default. Per-class and per-ability cone profiles
   * (warrior/paladin wide swings vs rogue near-single-target) are specced in
   * `docs/design/combat/melee-cone-profiles.md` — COMBAT-MELEE-CONE-02.
   */
  meleeConeRangePx: 150,
  attackRangeRanged: 800,
  knockbackForceX: 600,
  knockbackForceY: -400,
  targeting: {
    maxRange: 1200,
    tabCycleWindowMs: 500
  },
  /** @deprecated Import `MOB_AI` from `src/shared/config/mob-ai.config`. */
  mobAI: {
    aggroRange: MOB_AI.aggroRangePx,
    leashRange: MOB_AI.leashRangePx,
    attackRange: MOB_AI.attackRangePx,
    attackIntervalMs: MOB_AI.attackIntervalMs,
    attackDamage: MOB_AI.attackDamage,
    wanderChance: MOB_AI.wanderChance,
    wanderStopChance: MOB_AI.wanderStopChance,
    returnSpeed: MOB_AI.returnSpeed,
    deathDespawnMs: MOB_AI.deathDespawnMs,
    respawnMs: MOB_AI.respawnMs,
  },
  /** @deprecated Import `LOOT.pickup` from `src/shared/config/loot.config`. */
  loot: {
    vacuumRange: LOOT.pickup.vacuumRangePx,
    snappingSpeed: LOOT.pickup.snappingSpeedPxPerSec,
    pickupDistance: LOOT.pickup.pickupDistancePx,
    magnetRadius: LOOT.pickup.magnetRadiusPx,
    bobDuration: LOOT.pickup.bobDurationMs,
    bobOffset: LOOT.pickup.bobOffsetPx,
    sparkleLifespan: LOOT.pickup.sparkleLifespanMs,
    despawnTime: LOOT.pickup.despawnTimeMs,
  },

  // ── Server-side constants (previously scattered across handlers) ──────────

  /** Interaction ranges (px) */
  interactionRanges: {
    npc: 120,               // NPCInteractionHandler, SummonStoneHandler
    trade: 200,             // TradeHandler
    summonStone: 120,       // SummonStoneHandler
  },

  /** Anti-cheat caps for ability range/radius validation */
  antiCheat: {
    maxAreaRadius: 300,      // CombatHandler area ability radius cap
    maxMeleeRange: 200,      // CombatHandler melee range cap
    networkJitterMelee: 30,  // px tolerance for melee attack range
    networkJitterRanged: 50, // px tolerance for ranged attack range
  },

  /** Timer durations (ms) */
  timers: {
    tradeTimeoutMs: 60_000,
    summonStoneExpiryMs: 30_000,
    lootDespawnMs: 60_000,
    npcRespawnMs: 5_000,
    zoneTickMs: 30_000,
  },

  /** Item system defaults */
  items: {
    defaultMaxStack: 99,
  },

  /** Progression formulas */
  progression: {
    xpPerLevel: 100,          // XP threshold = level * this value
    statPointsPerLevel: 5,    // Stat points awarded per level-up
    minLevel: 1,
    maxLevel: 999,
  },

  /** NPC shop economics */
  shop: {
    sellPriceMultiplier: 0.4,  // sell price = 40% of buy price
  },

  /** Party system */
  party: {
    inviteExpiryMs: 30_000,    // 30 seconds
  },

  /** @deprecated Import `LOOT.drops` from `src/shared/config/loot.config`. */
  drops: {
    goldDropRate: LOOT.drops.goldDropRate,
    goldAmountMin: LOOT.drops.goldAmountMin,
    goldAmountMax: LOOT.drops.goldAmountMax,
    lootScatterRadius: LOOT.drops.lootScatterRadiusPx,
  },

  /** @deprecated Import `REGEN` from `src/shared/config/regen.config`. */
  regen: {
    /** Mutable copy: QA fixtures zero `health.inCombatPerSecond` and
     *  `health.outOfCombatPctPerSecond` (and `manaPerSecond`) on their own server. */
    health: { ...REGEN.health },
    manaPerSecond: REGEN.manaPerSecond,
  },
};
